import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiService } from '../../../../shared/services/api-service/api-service';

export interface IFilter {
  filterKey: string;
  label: string;
  filterOptions: Array<{ graphQlKey: string; label: string }>;
}

export interface IGrouping {
  graphQlKey: string;
  label: string;
}

export interface IFocus {
  graphQlKey: string;
  label: string;
  isDefault: boolean;
  category: string;
  categoryOrder: string;
  fociOrder: string;
  filters: IFilter[];
  groupings: IGrouping[];
}

export interface IPartnerGroupingsPayload {
  focusKey?: string;
  filterKeys: string[];
  groupingKey: string;
  columns: any[];
}

@Injectable()
export class ShelterDataService {
  private fociCache$: Observable<any> = null;
  private _shelterSubject: Subject<any> = new BehaviorSubject<any>([]);
  public readonly shelterSubject: Observable<Array<any>> = this._shelterSubject.asObservable();

  constructor(public apiService: ApiService) {}

  getShelterFocusData$(shelterId: string, focusKey?: string): Observable<{ data: { ShelterFocus: IFocus[] } }> {
    const payload = this.getShelterFocusQuery(shelterId, focusKey);
    if (!focusKey) {
      if (!this.fociCache$) this.fociCache$ = this.apiService.getStudentsGraphQL(payload).pipe(shareReplay(1));
      return this.fociCache$;
    } else {
      return this.apiService.getStudentsGraphQL(payload);
    }
  }

  getShelterGroupingData$(
    payload: { filterKeys: string[]; groupingKey: string; columns: string[] },
    shelterId: string,
  ) {
    // only want active students, so we append this filter behind the scenes
    payload.filterKeys.push('ACTIVE_ONLY');

    const query = this.generateQuery({ payload }, shelterId);
    return this.apiService.getStudentsGraphQL({ query, fetchPolicy: 'no-cache' });
  }

  getShelterFocusQuery(shelterId: string, focusKey?: string) {
    const payload = focusKey ? `shelterId:"${shelterId}", focusKey:"${focusKey}"` : `shelterId:"${shelterId}"`;

    const query = `{
      ShelterFocus(${payload}) {
        graphQlKey
        label
        isDefault
        category
        categoryOrder
        filters {
          label
          filterOptions {
            graphQlKey
            label
            isDefault
          }
        }
        groupings {
          graphQlKey
          label
          requiresWildcard
          columns {
            graphQlKey
            label
            requiresWildcard
            columnDataFormat
            cellConfig
            cellTooltip
            headerTooltip {
              ...on SimpleTooltip {
                simpleContent
              }
              ...on TableTooltip {
                tableContent {
                  headers
                  rowData
                }
              }
            }
          }
        }

      }
    }`;
    return { query, fetchPolicy: 'no-cache' };
  }

  setListData(listData) {
    this._shelterSubject = listData;
  }

  getListData() {
    return this.shelterSubject;
  }

  clearFociCache(): void {
    this.fociCache$ = null;
  }

  generateQuery({ payload }: { payload: IPartnerGroupingsPayload }, shelterId: string) {
    const { filterKeys, groupingKey, columns } = payload;

    const columnKeys: string[] = columns.map(({ graphQlKey }) => graphQlKey);
    // TODO: handle wildcard in groupingKey
    const filterKeysArray: string[] = filterKeys.reduce((accum: string[], key: string) => {
      if (key.includes('="')) {
        // Escape the wildcard value passed from filter dropdown, in addition to the one from rollup table
        const filterVal = key.split('=');
        const filterString = filterVal[1].replace(/\"/g, '\\"');
        accum.push(`"${filterVal[0]}=${filterString}"`);
      } else {
        accum.push(`"${key}"`);
      }
      return accum;
    }, []);
    return `{
      PartnerGroupings(partnerId: "${shelterId}", filterKeys: [${filterKeysArray}], groupingKey: "${groupingKey}", columns: ${JSON.stringify(
      columnKeys,
    )}){
          key
          human
          tooltip
          rowData {
            data
            meta
            columnKey
          } 
      }
    }
    `;
  }
}
